import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, Grid, Paper, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Collapse,
  Box,
  CircularProgress
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import * as Icons from '@material-ui/icons';
import { invokeApig } from '../lib/awsLib';
import NeokodeButton from '../components/NeokodeButton';
import { Chip } from '@material-ui/core';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  section: {
    margin: theme.spacing(3, 0),
    marginBottom: theme.spacing(10),
    width: 'inherit',
  },
  paper: {
    padding: theme.spacing(2),
  },
  gridMaxMd: {
    [theme.breakpoints.up('md')]: {
      maxWidth: theme.breakpoints.values.lg,
    },
  },
  filterContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  green: {
    backgroundColor: '#00b31f',
    color: '#ffffff',
  },
  yellow: {
    backgroundColor: '#ff9800',
    color: '#ffffff',
  },
  gray: {
    backgroundColor: '#aaa',
    color: '#ffffff',
  },
  red: {
    backgroundColor: '#b3001f',
    color: '#ffffff',
  },
  total: {
    marginTop: theme.spacing(2),
    textAlign: 'right',
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  clientRow: {
    backgroundColor: theme.palette.grey[100],
  },
  detailRow: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
  detailContent: {
    padding: theme.spacing(3),
  },
  refreshButton: {
    marginLeft: theme.spacing(2),
  },
  textRight: {
    textAlign: 'right',
  },
  nested: {
    paddingLeft: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

function Row({ client, totalByClient }) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Fila principal del cliente */}
      <TableRow className={classes.clientRow}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <Icons.KeyboardArrowUp /> : <Icons.KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={client.billable ? 'Sí' : 'No'}
            className={client.billable ? classes.green : classes.red}
          />
        </TableCell>
        <TableCell>{client.rut}</TableCell>
        <TableCell>{client.name}</TableCell>
        <TableCell align="right">${totalByClient.toLocaleString('es-CL')}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={client.status}
            className={(client.status === 'Facturado') ? classes.green : ((client.status === 'Pendiente') ? classes.gray : classes.yellow)}
          />
        </TableCell>
        <TableCell />
        <TableCell />
      </TableRow>

      {/* Fila de detalle de servicios */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={2}>
              <Typography variant="h6" gutterBottom>
                Detalle de Servicios
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Servicio</TableCell>
                    <TableCell>Frecuencia</TableCell>
                    <TableCell>Cantidad</TableCell>
                    <TableCell align="right">Precio Unitario</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {client.services.map((service) => (
                    <TableRow key={`${client.rut}-${service.code}`} className={classes.nested}>
                      <TableCell>{service.code}</TableCell>
                      <TableCell>{service.name}</TableCell>
                      <TableCell>{service.billing_frequency}</TableCell>
                      <TableCell>{service.current_month.quantity}</TableCell>
                      <TableCell align="right">
                        ${service.current_month.unit_price.toLocaleString('es-CL')}
                      </TableCell>
                      <TableCell align="right">
                        ${service.current_month.subtotal.toLocaleString('es-CL')}
                      </TableCell>
                      <TableCell>
                        {
                          !!service.current_month.status ?
                          <Chip
                            size="small"
                            label={service.current_month.status}
                            className={service.current_month.status === 'Facturado' ? classes.green : classes.yellow}
                          />
                          :
                          <Chip
                            size="small"
                            label={'Pendiente'}
                            className={classes.gray}
                          />
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function CustomersInvoicingPage() {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [isGlobalError, setIsGlobalError] = useState(false);
  const [message, setMessage] = useState('');
  const [clients, setClients] = useState([]);
  const [uf, setUf] = useState('');
  const [year, setYear] = useState(moment().year());
  const [month, setMonth] = useState(moment().month() + 1);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    getInvoicingData();
  }, [year, month]);

  const getInvoicingData = async () => {
    try {
      setIsLoading(true);
      setIsGlobalError(false);
      setClients([]);
      setTotalAmount(0);
      setUf(0);
      
      const response = await invokeApig({
        path: "/invoicing/all",
        method: "POST",
        body: {
          year,
          month
        }
      });

      if (response?.body?.clients) {
        setClients(response.body.clients);
        setUf(response.body.uf);
        
        // Calcular total general
        const total = response.body.clients.reduce((acc, client) => {
          return acc + client.services.reduce((serviceAcc, service) => {
            return serviceAcc + (service.current_month.subtotal || 0);
          }, 0);
        }, 0);
        
        setTotalAmount(total);
      } else {
        setMessage('Error al obtener los datos de facturación');
        setIsGlobalError(true);
      }
    } catch (error) {
      console.error('Error fetching invoicing data:', error);
      setMessage('Error al obtener los datos de facturación. Intenta nuevamente o comunícate con Soporte');
      setIsGlobalError(true);
    }
    setIsLoading(false);
  };

  const calculateTotalByClient = (client) => {
    if (client.services.length > 0) {
      const firstStatus = client.services[0].current_month.status;
      const allEquals = client.services.every(item => item.current_month.status === firstStatus);
      client.status = allEquals ? (firstStatus ? firstStatus : "Pendiente") : "Incompleto";
    } else {
      client.status = "Sin servicios";
    }
    return client.services.reduce((acc, service) => {
      return acc + (service.current_month.subtotal || 0);
    }, 0);
  };

  const months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => moment().year() - 2 + i);

  const renderError = () => (
    <>
      <Alert severity="error">
        {message}
      </Alert>
      <NeokodeButton
        type="button"
        variant="contained"
        className={classes.button}
        component={RouterLink}
        to="/"
      >
        Volver al inicio
      </NeokodeButton>
    </>
  );

  return (
    <Grid container spacing={2} className={classes.section} justifyContent="center">
      <Grid item xs={12} sm={12} md={12} lg={12} className={classes.gridMaxMd}>
        <Paper className={classes.paper}>
          <Typography variant="h4" gutterBottom>
            Facturación de Servicios
          </Typography>

          {isGlobalError ? (
            renderError()
          ) : (
            <>
              <div className={classes.filterContainer}>
                <TextField
                  select
                  label="Año"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Mes"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </TextField>
                <IconButton 
                  className={classes.refreshButton}
                  onClick={getInvoicingData}
                  disabled={isLoading}
                >
                  <Icons.Refresh />
                </IconButton>
                {
                  !isLoading &&
                  <Grid>
                    Valor UF: ${uf.toLocaleString('es-CL')}
                  </Grid>
                }
              </div>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headerCell} />
                      <TableCell className={classes.headerCell}>Activo</TableCell>
                      <TableCell className={classes.headerCell}>RUT</TableCell>
                      <TableCell className={classes.headerCell}>Cliente</TableCell>
                      <TableCell className={classes.headerCell} align="right">Total</TableCell>
                      <TableCell className={classes.headerCell}>Estado</TableCell>
                      <TableCell className={classes.headerCell} />
                      <TableCell className={classes.headerCell} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {
                      isLoading ?
                      <TableRow className={classes.clientRow}>
                        <TableCell colSpan={8} align='center'>
                          <CircularProgress /> Cargando datos, por favor espera...
                        </TableCell>
                      </TableRow>
                      :
                      (
                        clients.length === 0 ?
                        <TableRow className={classes.clientRow}>
                          <TableCell colSpan={8} align='center'>
                            <Box>
                              <Box col>
                                <Icons.Warning style={{color: 'orange'}}/>
                              </Box>
                              <Box col>
                                No hay datos disponibles para la fecha seleccionada
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                        :
                        clients.map((client) => (
                          <Row 
                            key={client.rut} 
                            client={client} 
                            totalByClient={calculateTotalByClient(client)}
                          />
                        ))
                      )
                    }
                  </TableBody>
                </Table>
              </TableContainer>

              <div className={classes.total}>
                <Typography variant="h6">
                  Total General: ${totalAmount.toLocaleString('es-CL')}
                </Typography>
              </div>
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}

export default CustomersInvoicingPage;